import { useAppStore } from '@store/appStore';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { NavigationHistoryPanel, PlaybackPanel } from '../NavigationHistory';
import './NavigationMenu.css';

interface NavigationMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenSettings: () => void;
  isOwner?: boolean;
  shopId?: string;
}

export function NavigationMenu({ isOpen, onClose, onOpenSettings, isOwner = false, shopId }: NavigationMenuProps) {
  const { t } = useTranslation('viewer');
  const cameras = useAppStore((state) => state.cameras);
  const currentCameraIndex = useAppStore((state) => state.currentCameraIndex);
  const setSelectedShop = useAppStore((state) => state.setSelectedShop);
  const showDebugOverlay = useAppStore((state) => state.showDebugOverlay);
  const toggleDebugOverlay = useAppStore((state) => state.toggleDebugOverlay);
  const activeUserSectionId = useAppStore((state) => state.activeUserSectionId);
  const shopDatasets = useAppStore((state) => state.shopDatasets);
  
  const handleBackToShops = () => {
    setSelectedShop(null);
    onClose();
  };
  
  if (!isOpen) return null;
  
  const menuContent = (
    <>
      {/* Backdrop */}
      <div 
        className="navigation-menu__backdrop" 
        aria-hidden="true"
      />
      
      {/* Menu Panel */}
      <div className="navigation-menu">
        <div className="navigation-menu__header">
          <h2>{t('navigation')}</h2>
          <button 
            className="navigation-menu__close"
            onClick={onClose}
            aria-label={t('closeMenu')}
          >
            <svg 
              width="24" 
              height="24" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        
        <div className="navigation-menu__content">
          {/* Camera Counter */}
          <div className="navigation-menu__section">
            <div className="camera-counter">
              <div className="camera-counter__label">{t('currentFrame')}</div>
              <div className="camera-counter__value">
                {currentCameraIndex + 1} / {cameras.length}
              </div>
              <div className="camera-counter__progress">
                <div 
                  className="camera-counter__progress-bar"
                  style={{ 
                    width: `${((currentCameraIndex + 1) / cameras.length) * 100}%` 
                  }}
                />
              </div>
            </div>
          </div>
          
          {/* Navigation History & Playback */}
          <div className="navigation-menu__section">
            <NavigationHistoryPanel />
            <PlaybackPanel />
          </div>
          
          {/* Navigation Buttons */}
          <div className="navigation-menu__section">
            <button 
              className="navigation-menu__button"
              onClick={onOpenSettings}
            >
              <svg 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="3" />
                <path d="M12 1v6m0 6v6m-9-9h6m6 0h6" />
                <path d="M12 1L9 4l3 3 3-3-3-3zm0 22l-3-3 3-3 3 3-3 3zM1 12l3-3 3 3-3 3-3-3zm22 0l-3-3-3 3 3 3 3-3z" />
              </svg>
              <span>{t('settings')}</span>
            </button>
            
            {/* Owner-only: Rescan current section */}
            {isOwner && activeUserSectionId && shopId && (() => {
              const dataset = shopDatasets.find(d => d.sectionId === activeUserSectionId);
              const sectionName = (dataset as any)?.sectionName || 'section';
              return (
                <button
                  className="navigation-menu__button"
                  onClick={() => {
                    onClose();
                    const params = new URLSearchParams({
                      shopId: shopId,
                      sectionId: activeUserSectionId,
                      sectionName: sectionName,
                      mode: 'rescan',
                    });
                    window.location.href = `/scan?${params.toString()}`;
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="1 4 1 10 7 10" />
                    <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                  </svg>
                  <span>Rescan {sectionName}</span>
                </button>
              );
            })()}
            
            <button 
              className="navigation-menu__button"
              onClick={handleBackToShops}
            >
              <svg 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
              <span>{t('backToShopSelector')}</span>
            </button>
          </div>
          
          {/* Debug Mode Toggle */}
          <div className="navigation-menu__section">
            <label className="navigation-menu__label">
              {t('debugMode')}
            </label>
            <button
              className={`navigation-menu__toggle ${showDebugOverlay ? 'active' : ''}`}
              onClick={toggleDebugOverlay}
              aria-pressed={showDebugOverlay}
            >
              <div className="navigation-menu__toggle-track">
                <div className="navigation-menu__toggle-thumb" />
              </div>
              <span>{showDebugOverlay ? t('common:on') : t('common:off')}</span>
            </button>
            <p className="navigation-menu__description">
              {t('debugModeDescription')}
            </p>
          </div>
          
          {/* Help Text */}
          <div className="navigation-menu__section navigation-menu__help">
            <h3>{t('gestureControls')}</h3>
            <ul>
              <li><strong>{t('swipeLeftLabel')}</strong> {t('swipeLeftAction')}</li>
              <li><strong>{t('swipeRightLabel')}</strong> {t('swipeRightAction')}</li>
              <li><strong>{t('swipeUpLabel')}</strong> {t('swipeUpAction')}</li>
              <li><strong>{t('swipeDownLabel')}</strong> {t('swipeDownAction')}</li>
            </ul>
          </div>
        </div>
      </div>
    </>
  );
  
  // Use createPortal to render outside the gesture-viewer container
  return createPortal(menuContent, document.body);
}
